import React, { useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import { connect } from 'react-redux';
import { MAINTENANCE_PLANNING_PATH } from '../../../../config/paths';
import {
  clearMaintenancePlanning,
  getMaintenancePlanning,
  putMaintenancePlanning,
} from '../../../../actions/mechanical-maintenance';
import {
  breadcrumbsPropTypes,
  matchPropTypes,
} from '../../../common/resource/proptypes/CommonPropTypes';
import Content from '../../../layout/Content';
import Loader from '../../../common/Loader';
import NoDataResource from '../../../common/resource/NoDataResource';
import {
  MAINTENANCE_FACTOR_TYPE,
  MAINTENANCE_MONTH_WEEK_NUMBERS,
  MAINTENANCE_PLANNING_TYPE,
  MAINTENANCE_RESET_RULE_TYPE,
  MAINTENANCE_WEEK_DAYS,
} from '../../../../config/constants';
import MaintenancePlanningForm from './MaintenancePlanningForm';

const EditMaintenancePlanning = ({
  breadcrumbs,
  dispatchGetMaintenancePlanning,
  dispatchClearMaintenancePlanning,
  dispatchPutMaintenancePlanning,
  match: {
    params: { id: maintenancePlanningId },
  },
  loading,
  maintenancePlanning,
}) => {
  useLayoutEffect(() => {
    dispatchGetMaintenancePlanning({ maintenancePlanningId });

    return () => dispatchClearMaintenancePlanning();
  }, []);

  const normalizeToArray = (value) => {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
  };

  const onSubmit = (formValues) => {
    const maintenancePlanningResourceList = [];

    const registeredBusIds = normalizeToArray(formValues.registeredBusId);
    const maintenanceBusGroupIds = normalizeToArray(
      formValues.maintenanceBusGroupId,
    );

    if (registeredBusIds.length > 0) {
      registeredBusIds.forEach((bus) => {
        maintenancePlanningResourceList.push({
          registeredBusId: bus.value,
          maintenanceBusGroupId: null,
        });
      });
    } else if (maintenanceBusGroupIds.length > 0) {
      maintenanceBusGroupIds.forEach((maintenanceGroup) => {
        maintenancePlanningResourceList.push({
          registeredBusId: null,
          maintenanceBusGroupId: maintenanceGroup.value,
        });
      });
    }

    const newFormValues = { ...formValues };
    newFormValues.planningType = formValues.planningType.value;
    newFormValues.factorType = formValues.factorType.value;
    newFormValues.companyAreaId = formValues.companyArea.value;
    newFormValues.maintenancePlanningActivityOrderList = Array.isArray(
      formValues.maintenancePlanningActivityOrderList,
    )
      ? formValues.maintenancePlanningActivityOrderList.map((activity) => ({
          activityId: activity.activity.value,
          factor: activity.factor,
          activityOrder: activity.activityOrder,
        }))
      : [];
    newFormValues.maintenancePlanningResourceList =
      maintenancePlanningResourceList;

    newFormValues.maintenancePlanningResetRule = formValues.resetRuleType
      ? {
          maintenancePlanningResetRuleType: formValues.resetRuleType.value,
          dayOfMonth: formValues.dayOfMonth ? formValues.dayOfMonth : null,
          weekday: formValues.weekday ? formValues.weekday.value : null,
          rangeStart: formValues.rangeStart ? formValues.rangeStart : null,
          rangeEnd: formValues.rangeEnd ? formValues.rangeEnd : null,
          weekNumber: formValues.weekNumber
            ? formValues.weekNumber.value
            : null,
        }
      : null;

    dispatchPutMaintenancePlanning(maintenancePlanningId, newFormValues);
  };

  const generateInitialValues = ({
    description = '',
    planningType = null,
    resetFactor = 0,
    percentageDeviation = 0,
    deviationDays = 0,
    factorType = null,
    companyArea = null,
    maintenancePlanningActivityOrderList = [],
    maintenancePlanningResourceList = [],
    maintenancePlanningResetRule = null,
  }) => {
    const createOption = (id, label) => ({ value: id, label });

    const findEnumValue = (enumData, targetValue) => {
      if (!enumData || targetValue === null || targetValue === undefined)
        return null;

      const values = Array.isArray(enumData)
        ? enumData
        : Object.values(enumData);

      return values.find((item) => item.value === targetValue) || null;
    };

    // Procesamiento de recursos
    let maintenanceBusGroupId = null;
    let registeredBusId = null;

    if (Array.isArray(maintenancePlanningResourceList)) {
      const busGroup = maintenancePlanningResourceList.find(
        (resource) => resource.maintenanceBusGroup,
      );
      if (busGroup) {
        maintenanceBusGroupId = createOption(
          busGroup.maintenanceBusGroup.id,
          busGroup.maintenanceBusGroup.name,
        );
      }

      const bus = maintenancePlanningResourceList.find(
        (resource) => resource.registeredBus,
      );
      if (bus) {
        registeredBusId = createOption(
          bus.registeredBus.id,
          `${bus.registeredBus.companyBusId} - ${bus.registeredBus.licensePlate}`,
        );
      }
    }

    let resetRuleValues = {};
    if (maintenancePlanningResetRule) {
      resetRuleValues = {
        showResetRuleDays: true,
        resetRuleType:
          MAINTENANCE_RESET_RULE_TYPE[
            maintenancePlanningResetRule.maintenancePlanningResetRuleType
          ],
        dayOfMonth: maintenancePlanningResetRule.dayOfMonth,
        weekday: findEnumValue(
          MAINTENANCE_WEEK_DAYS,
          maintenancePlanningResetRule.weekday,
        ),
        rangeStart: maintenancePlanningResetRule.rangeStart,
        rangeEnd: maintenancePlanningResetRule.rangeEnd,
        weekNumber: findEnumValue(
          MAINTENANCE_MONTH_WEEK_NUMBERS,
          maintenancePlanningResetRule.weekNumber,
        ),
      };
    }

    return {
      description,
      planningType: planningType && MAINTENANCE_PLANNING_TYPE[planningType],
      resetFactor,
      percentageDeviation,
      deviationDays,
      factorType: factorType && MAINTENANCE_FACTOR_TYPE[factorType],
      companyArea:
        companyArea && createOption(companyArea.id, companyArea.name),
      maintenancePlanningActivityOrderList:
        maintenancePlanningActivityOrderList.map((activity) => ({
          id: activity.id,
          activity: createOption(
            activity.activity.id,
            `${activity.activity.companyActivityId} - ${activity.activity.name}`,
          ),
          factor: activity.factor,
          activityOrder: activity.activityOrder,
        })),
      maintenanceBusGroupId,
      registeredBusId,
      ...resetRuleValues,
    };
  };

  let content;

  if (loading) content = <Loader />;
  else if (maintenancePlanning.isEmpty())
    content = <NoDataResource returnPage={MAINTENANCE_PLANNING_PATH} />;
  else
    content = (
      <MaintenancePlanningForm
        onSubmit={onSubmit}
        initialValues={generateInitialValues(maintenancePlanning.toJS())}
      />
    );

  return (
    <Content
      breadcrumbs={breadcrumbs}
      title="Editar Plan de Mantenimiento"
      subtitle="Editar plan de mantenimiento"
      content={content}
    />
  );
};

EditMaintenancePlanning.propTypes = {
  breadcrumbs: breadcrumbsPropTypes.isRequired,
  dispatchClearMaintenancePlanning: PropTypes.func.isRequired,
  dispatchGetMaintenancePlanning: PropTypes.func.isRequired,
  dispatchPutMaintenancePlanning: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  match: matchPropTypes.isRequired,
  maintenancePlanning: PropTypes.instanceOf(Immutable.Map).isRequired,
};

EditMaintenancePlanning.defaultProps = {
  loading: false,
};

const mapDispatchToProps = {
  dispatchClearMaintenancePlanning: clearMaintenancePlanning,
  dispatchGetMaintenancePlanning: getMaintenancePlanning,
  dispatchPutMaintenancePlanning: putMaintenancePlanning,
};

const mapStateToProps = (
  { MechanicalMaintenanceUnit },
  {
    match: {
      params: { id },
    },
  },
) => ({
  breadcrumbs: [
    ...MechanicalMaintenanceUnit.UnitHome.get('breadcrumbs'),
    {
      text: 'Planes de Mantenimiento',
      href: MAINTENANCE_PLANNING_PATH,
    },
    {
      text: 'Ver',
      href: `${MAINTENANCE_PLANNING_PATH}/${id}`,
    },
    {
      text: 'Editar',
      href: '',
    },
  ],
  maintenancePlanning: MechanicalMaintenanceUnit.MaintenancePlanning.getIn([
    'current',
    'content',
  ]),
  loading: !MechanicalMaintenanceUnit.MaintenancePlanning.getIn([
    'current',
    'activity',
  ]).isEmpty(),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(EditMaintenancePlanning);
